import { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TooltipProps, TypographyProps } from '@mui/material';
import type { ReactNode } from 'react';

type EllipsisLabelProps = {
    label: ReactNode;
    tooltipTitle?: ReactNode;
    tooltipPlacement?: TooltipProps['placement'];
    sx?: SxProps<Theme>;
    typographyProps?: Omit<TypographyProps, 'children' | 'sx' | 'ref'>;
};

export function EllipsisLabel({
    label,
    tooltipTitle,
    tooltipPlacement = 'top',
    sx,
    typographyProps,
}: EllipsisLabelProps) {
    const textRef = useRef<HTMLSpanElement | null>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
        const el = textRef.current;
        if (!el) return;

        // +1 是为了避免小数像素导致判断不稳定
        const overflow = el.scrollWidth > el.clientWidth + 1;

        setIsOverflow(overflow);
    }, []);

    useEffect(() => {
        checkOverflow();

        const rafId = window.requestAnimationFrame(checkOverflow);
        const timerId = window.setTimeout(checkOverflow, 80);

        window.addEventListener('resize', checkOverflow);

        let resizeObserver: ResizeObserver | null = null;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(checkOverflow);

            if (textRef.current) {
                resizeObserver.observe(textRef.current);

                if (textRef.current.parentElement) {
                    resizeObserver.observe(textRef.current.parentElement);
                }
            }
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            window.clearTimeout(timerId);
            window.removeEventListener('resize', checkOverflow);
            resizeObserver?.disconnect();
        };
    }, [checkOverflow, label]);

    const content = (
        <Typography
            ref={textRef}
            component="span"
            {...typographyProps}
            sx={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                cursor: isOverflow ? 'pointer' : 'default',
                ...sx,
            }}
        >
            {label}
        </Typography>
    );

    return (
        <Tooltip
            title={tooltipTitle ?? label}
            arrow
            placement={tooltipPlacement}
            disableHoverListener={!isOverflow}
            disableFocusListener={!isOverflow}
            disableTouchListener={!isOverflow}
            slotProps={{
                popper: {
                    sx: {
                        zIndex: 99999,
                    },
                },
            }}
        >
            {content}
        </Tooltip>
    );
}